import { Descriptions, Flex, Form, InputNumber, message, Modal, Table, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useGetPaymentAllocationsListQuery } from "features/payments/paymentsAllocations";
import { addAmounts, camelToSnakeCase, formatCurrency } from "common/utils";
import currency from "currency.js";
import { useLazyFetchAccountsQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { useReallocatePaymentIntentsMutation } from "features/payments/paymentsAPI";
import { useLazyFetchCreditorsQuery } from "features/creditors/agencyPortal/creditorsAPI";

function PaymentAllocationItem({
  accountId,
  category,
  originalValue,
  form,
  currentValue,
  isDisabled,
}) {
  useEffect(() => {
    form.setFieldValue([accountId, category], currentValue || 0);
  }, [accountId, category, form, currentValue]);

  return (
    <Flex vertical gap={4}>
      <Form.Item
        name={[accountId, category]}
        normalize={(value) => (value === null || value === "" || value === undefined ? 0 : value)}
        noStyle
      >
        <InputNumber
          prefix="$"
          precision={2}
          min={0}
          max={originalValue}
          // disabled if payment category is not in the payment category priority for the creditor setup for the account
          disabled={isDisabled}
        />
      </Form.Item>
      <Typography.Text type="secondary">{formatCurrency(originalValue)}</Typography.Text>
    </Flex>
  );
}

export default function PaymentAllocationBreakdownModal({
  paymentIntentId,
  onOk,
  title = "Payment Allocation Breakdown",
  onCancel,
}) {
  const [form] = Form.useForm();
  const [fetchAccounts, { isLoading: isFetchAccountsLoading }] = useLazyFetchAccountsQuery();
  const [reallocatePaymentIntents, { isLoading: isReallocatePaymentIntentsLoading }] =
    useReallocatePaymentIntentsMutation();
  const [fetchCreditors] = useLazyFetchCreditorsQuery();
  const [accounts, setAccounts] = useState([]);
  const [currentCreditors, setCurrentCreditors] = useState([]);
  const { data: paymentAllocations, isLoading: isPaymentAllocationsLoading } =
    useGetPaymentAllocationsListQuery(
      {
        includePaymentIntentIds: [paymentIntentId],
      },
      { skip: !paymentIntentId },
    );

  useEffect(() => {
    async function fetchData() {
      if (paymentAllocations?.results?.length > 0) {
        const accountIds = paymentAllocations.results.map((allocation) => allocation.accountId);
        const result = await fetchAccounts({
          filters: {
            accountIds,
          },
        });
        if ("data" in result) {
          setAccounts(result?.data?.results);
          const creditorIds = result?.data?.results.map((account) => account.creditorId);
          const response = await fetchCreditors({ filters: { creditorIds } });
          if ("data" in response) {
            setCurrentCreditors(response?.data?.results);
          }
        }
      }
    }
    fetchData();
  }, [paymentAllocations, fetchAccounts, fetchCreditors]);

  const allValues = Form.useWatch([], form);

  const totalAmount = useMemo(() => {
    if (!paymentAllocations || paymentAllocations.results.length === 0) {
      return 0;
    }
    return currency(
      paymentAllocations.results.reduce(
        (sum, allocation) => sum + (currency(allocation.totalAmount, { precision: 2 }).value || 0),
        0,
      ),
      { precision: 2 },
    ).value;
  }, [paymentAllocations]);

  const allocatedAmount = useMemo(() => {
    if (!allValues) {
      return 0;
    }

    const total = Object.values(allValues)
      .flatMap((account) => Object.values(account))
      .reduce((sum, val) => sum + (val || 0), 0);

    return currency(total, { precision: 2 }).value;
  }, [allValues]);

  const onSubmit = async () => {
    const difference = Math.abs(currency(totalAmount).subtract(allocatedAmount).value);
    if (difference >= 0.01) {
      return Modal.error({
        title: "Error",
        content: "The allocated amount must equal the total payment amount.",
        okText: "OK",
      });
    }

    const result = await reallocatePaymentIntents({
      paymentIntentId,
      accountToReallocationBreakdownMap: Object.fromEntries(
        Object.entries(allValues).filter(
          ([_, value]) => value && Object.values(value).some((v) => v != null),
        ),
      ),
    });
    if ("error" in result) {
      return;
    }
    if ("data" in result) {
      message.success("Payment intent(s) reallocated successfully");
      await onOk();
    }
  };
  const columns = [
    {
      title: "Account",
      dataIndex: "externalId",
      key: "externalId",
      fixed: "left",
      width: 200,
      render: (text, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <Flex vertical gap={4}>
            <Typography.Text>Account #{account?.externalId}</Typography.Text>
            <Typography.Text>
              {formatCurrency(addAmounts(account?.totalBalance, record.totalAmount))}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: "Principal",
      key: "principal",
      render: (text, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("principal"))
            }
            form={form}
            category="principal"
            accountId={account?.id}
            originalValue={addAmounts(account?.principalBalance, record.principal)}
            currentValue={record.principal}
          />
        );
      },
    },
    {
      title: "Agency Interest",
      key: "interest",
      render: (text, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("interest"))
            }
            form={form}
            category="interest"
            accountId={account?.id}
            originalValue={addAmounts(account?.interestBalance, record.interest)}
            currentValue={record.interest}
          />
        );
      },
    },
    {
      title: "Client Interest",
      key: "clientInterest",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("clientInterest"))
            }
            form={form}
            category="clientInterest"
            accountId={account?.id}
            originalValue={addAmounts(account?.clientInterestBalance, record.clientInterest)}
            currentValue={record.clientInterest}
          />
        );
      },
    },
    {
      title: "Prejudgment Interest",
      key: "prejudgmentInterest",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(
                  camelToSnakeCase("prejudgmentInterest"),
                )
            }
            form={form}
            category="prejudgmentInterest"
            accountId={account?.id}
            originalValue={addAmounts(
              account?.prejudgmentInterestBalance,
              record.prejudgmentInterest,
            )}
            currentValue={record.prejudgmentInterest}
          />
        );
      },
    },
    {
      title: "Attorney Fees",
      key: "attorneyFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("attorneyFees"))
            }
            form={form}
            category="attorneyFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.attorneyFeesBalance, record.attorneyFees)}
            currentValue={record.attorneyFees}
          />
        );
      },
    },
    {
      title: "Check Fees",
      key: "checkFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("checkFees"))
            }
            form={form}
            category="checkFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.checkFeesBalance, record.checkFees)}
            currentValue={record.checkFees}
          />
        );
      },
    },
    {
      title: "Collection Fees",
      key: "collectionFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("collectionFees"))
            }
            form={form}
            category="collectionFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.collectionFeesBalance, record.collectionFees)}
            currentValue={record.collectionFees}
          />
        );
      },
    },
    {
      title: "Court Fees",
      key: "courtFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("courtFees"))
            }
            form={form}
            category="courtFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.courtFeesBalance, record.courtFees)}
            currentValue={record.courtFees}
          />
        );
      },
    },
    {
      title: "Handling Fees",
      key: "handlingFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("handlingFees"))
            }
            form={form}
            category="handlingFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.handlingFeesBalance, record.handlingFees)}
            currentValue={record.handlingFees}
          />
        );
      },
    },
    {
      title: "Legal Fees",
      key: "legalFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("legalFees"))
            }
            form={form}
            category="legalFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.legalFeesBalance, record.legalFees)}
            currentValue={record.legalFees}
          />
        );
      },
    },
    {
      title: "Lien Fees",
      key: "lienFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("lienFees"))
            }
            form={form}
            category="lienFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.lienFeesBalance, record.lienFees)}
            currentValue={record.lienFees}
          />
        );
      },
    },
    {
      title: "Misc Fees",
      key: "miscFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("miscFees"))
            }
            form={form}
            category="miscFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.miscFeesBalance, record.miscFees)}
            currentValue={record.miscFees}
          />
        );
      },
    },
    {
      title: "Service Fees",
      key: "serviceFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("serviceFees"))
            }
            form={form}
            category="serviceFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.serviceFeesBalance, record.serviceFees)}
            currentValue={record.serviceFees}
          />
        );
      },
    },
    {
      title: "Client Fees",
      key: "clientFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase("clientFees"))
            }
            form={form}
            category="clientFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.clientFeesBalance, record.clientFees)}
            currentValue={record.clientFees}
          />
        );
      },
    },
    {
      title: "Garnishment Fees",
      key: "garnishmentFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(
                  camelToSnakeCase("garnishmentFees"),
                )
            }
            form={form}
            category="garnishmentFees"
            accountId={account?.id}
            originalValue={addAmounts(account?.garnishmentFeesBalance, record.garnishmentFees)}
            currentValue={record.garnishmentFees}
          />
        );
      },
    },
    {
      title: "Postjudgment Attorney Fees",
      key: "postjudgmentAttorneyFees",
      render: (_, record, index) => {
        const account = accounts.find((a) => a.id === record.accountId);
        return (
          <PaymentAllocationItem
            isDisabled={
              !currentCreditors
                ?.find((creditor) => creditor.id === account?.creditorId)
                ?.paymentConfig.paymentCategoryPriority.includes(
                  camelToSnakeCase("postjudgmentAttorneyFees"),
                )
            }
            form={form}
            category="postjudgmentAttorneyFees"
            accountId={account?.id}
            originalValue={addAmounts(
              account?.postjudgmentAttorneyFeesBalance,
              record.postjudgmentAttorneyFees,
            )}
            currentValue={record.postjudgmentAttorneyFees}
          />
        );
      },
    },
  ];

  // filter out all columns where they are disabled for all accounts
  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          currentCreditors.some((creditor) =>
            creditor.paymentConfig.paymentCategoryPriority.includes(camelToSnakeCase(column.key)),
          ) || column.key === "externalId",
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCreditors],
  );

  return (
    <Modal
      okText="Confirm"
      cancelText="Cancel"
      maskClosable={false}
      title={title}
      open={!!paymentIntentId}
      onOk={onSubmit}
      onCancel={onCancel}
      width={800}
      confirmLoading={isReallocatePaymentIntentsLoading}
    >
      <Flex vertical gap={16}>
        <Form form={form}>
          <Table
            loading={isPaymentAllocationsLoading || isFetchAccountsLoading}
            scroll={{ x: "max-content" }}
            pagination={false}
            dataSource={paymentAllocations?.results}
            rowKey="id"
            // @ts-ignore
            columns={filteredColumns}
          />
        </Form>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Payment Amount">
            {formatCurrency(totalAmount)}
          </Descriptions.Item>
          <Descriptions.Item label="Allocated Amount">
            {formatCurrency(allocatedAmount)}
          </Descriptions.Item>
          <Descriptions.Item
            label="Remaining Amount"
            contentStyle={{
              color:
                allocatedAmount > currency(totalAmount, { precision: 2 }).value ? "red" : "inherit",
            }}
          >
            {formatCurrency(
              currency(totalAmount, { precision: 2 }).subtract(allocatedAmount).value,
            )}
          </Descriptions.Item>
        </Descriptions>
      </Flex>
    </Modal>
  );
}
